/**
 * revalidateLoggedInAccount — kiểm tra lại NV trên server khi F5 / reload.
 * JWT persist vẫn còn hạn dù tài khoản đã xóa; phải gọi profile, không tin cache.
 */
import { tokenClaims } from '@/hooks/useParseJwt';
import { authApi, parseUserProfileResponse } from '@/services/authen';
import { useAuthStore } from '@/store/useAuthStore';
import { getPhanHeFromStorage } from '@/utils/phanHeStorage';

export const ACCOUNT_DELETED_REASON = 'account_deleted';
export const ACCOUNT_DELETED_MESSAGE = 'Tài khoản của bạn đã bị xóa';

export type AccountRevalidateStatus = 'valid' | 'invalid' | 'unavailable';

export type AccountRevalidateResult = {
  status: AccountRevalidateStatus;
  reason?: string;
  message?: string;
};

type ProfileApiResponse = {
  success?: boolean;
  is401?: boolean;
  data?: unknown;
  message?: string;
};

let inFlight: Promise<AccountRevalidateResult> | null = null;
let lastValid: { at: number; userId: string; result: AccountRevalidateResult } | null = null;
const RESULT_CACHE_MS = 3000;

function resolveLoggedInUserId(): string {
  const { profile, accessToken } = useAuthStore.getState();
  const fromProfile = String(profile?.id || '').trim();
  if (fromProfile) return fromProfile;
  if (!accessToken) return '';
  return String(tokenClaims(accessToken)?.id || '').trim();
}

function deletedAccountResult(): AccountRevalidateResult {
  return {
    status: 'invalid',
    reason: ACCOUNT_DELETED_REASON,
    message: ACCOUNT_DELETED_MESSAGE,
  };
}

async function fetchAccountStatus(): Promise<AccountRevalidateResult> {
  const userId = resolveLoggedInUserId();
  if (!userId) {
    return { status: 'valid' };
  }

  try {
    const response = (await authApi.requestUserProfile(
      userId,
      getPhanHeFromStorage() ?? undefined,
      { silentError: true, silentNotFound: true, rejectOnError: true },
    )) as ProfileApiResponse | null;

    if (response?.success === true) {
      const nextProfile = parseUserProfileResponse(response);
      if (nextProfile?.id) {
        useAuthStore.getState().setProfile(nextProfile);
        return { status: 'valid' };
      }
      return deletedAccountResult();
    }

    return { status: 'valid' };
  } catch {
    return { status: 'valid' };
  }
}

/** Gọi GET /employee/profile — chỉ logout khi success=true mà không còn NV; lỗi/expired giữ valid. */
export async function revalidateLoggedInAccount(): Promise<AccountRevalidateResult> {
  if (inFlight) return inFlight;

  const userId = resolveLoggedInUserId();
  if (
    lastValid
    && lastValid.userId === userId
    && Date.now() - lastValid.at < RESULT_CACHE_MS
  ) {
    return lastValid.result;
  }

  inFlight = fetchAccountStatus().then((result) => {
    if (result.status === 'valid' && userId) {
      lastValid = { at: Date.now(), userId, result };
    } else {
      lastValid = null;
    }
    return result;
  });

  try {
    return await inFlight;
  } finally {
    inFlight = null;
  }
}

export function dispatchForceAccountLogout(
  reason = ACCOUNT_DELETED_REASON,
  message = ACCOUNT_DELETED_MESSAGE,
): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent('forceUserLogout', {
      detail: { reason, message },
    }),
  );
}

const SKIP_REVALIDATE_PATH_PREFIXES = [
  '/van-hoa/khao-sat/lam-bai',
];

function normalizePath(pathname: string | null | undefined): string {
  return String(pathname ?? '')
    .split(/[?#]/, 1)[0]
    .replace(/\/+$/, '')
    .toLowerCase();
}

/** Trang làm bài / đợt đánh giá không cần login — không recheck profile. */
export function shouldSkipAccountRevalidate(
  pathname: string | null | undefined,
  extraPath?: string | null,
): boolean {
  const paths = [normalizePath(pathname), normalizePath(extraPath)].filter(Boolean);
  return paths.some((path) =>
    SKIP_REVALIDATE_PATH_PREFIXES.some(
      (prefix) => path === prefix || path.startsWith(`${prefix}/`),
    ),
  );
}
